import React, { ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SearchField from 'ui/atoms/SearchField/SearchField'
import type { ComponentDocMap } from 'ui/uiConfig/docs'
import { getAliasDocs, getCommonPropDocs, getDocsForComponent, getDsiDocs, getRootFieldDocs } from 'ui/uiConfig/docs'
import styles from './UiConfigDocsPanel.module.less'

type DocTab = 'props' | 'dsi' | 'aliases' | 'root'

type Props = {
  componentName: string | undefined
  highlightKey?: string
  isOpen: boolean
  onClose: () => void
}

function EntryList({
  docs,
  search,
  highlightKey,
}: {
  docs: ComponentDocMap
  search: string
  highlightKey?: string
}): ReactElement {
  const { t } = useTranslation()
  const entries = useMemo(() => {
    const lower = search.toLowerCase()
    return Object.entries(docs).filter(
      ([key, doc]) => !lower || key.toLowerCase().includes(lower) || doc.description.toLowerCase().includes(lower)
    )
  }, [docs, search])

  if (entries.length === 0) {
    return <div className={styles.UiConfigDocsPanel_Empty}>{t('No matching documentation found.')}</div>
  }

  return (
    <>
      {entries.map(([key, doc]) => (
        <div
          key={key}
          className={`${styles.UiConfigDocsPanel_Entry}${highlightKey === key ? ` ${styles.UiConfigDocsPanel_Entry__highlighted}` : ''}`}
        >
          <div className={styles.UiConfigDocsPanel_EntryKey}>{key}</div>
          {doc.type && <div className={styles.UiConfigDocsPanel_EntryType}>{doc.type}</div>}
          <div className={styles.UiConfigDocsPanel_EntryDesc}>{doc.description}</div>
          {doc.example && <div className={styles.UiConfigDocsPanel_EntryExample}>{doc.example}</div>}
        </div>
      ))}
    </>
  )
}

export default function UiConfigDocsPanel({ componentName, highlightKey, isOpen, onClose }: Props): ReactElement {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<DocTab>('props')
  const [search, setSearch] = useState('')

  const componentDocs = useMemo(
    () => (componentName ? (getDocsForComponent(componentName) ?? getCommonPropDocs()) : getCommonPropDocs()),
    [componentName]
  )

  const tabDocs: ComponentDocMap = useMemo(() => {
    switch (activeTab) {
      case 'props':
        return componentDocs
      case 'dsi':
        return getDsiDocs()
      case 'aliases':
        return getAliasDocs()
      case 'root':
        return getRootFieldDocs()
    }
  }, [activeTab, componentDocs])

  if (!isOpen) return <></>

  return (
    <div className={styles.UiConfigDocsPanel}>
      <div className={styles.UiConfigDocsPanel_Header}>
        <span className={styles.UiConfigDocsPanel_Title}>
          {t('Documentation')}
          {componentName ? ` — ${componentName}` : ''}
        </span>
        <button type="button" onClick={onClose} aria-label={t('Close documentation panel')}>
          ✕
        </button>
      </div>

      <div className={styles.UiConfigDocsPanel_SearchWrapper}>
        <SearchField value={search} onChange={(value) => setSearch(String(value))} onClear={() => setSearch('')} />
      </div>

      <div className={styles.UiConfigDocsPanel_Tabs}>
        <button
          type="button"
          className={activeTab === 'props' ? styles['__active'] : ''}
          onClick={() => setActiveTab('props')}
        >
          {t('Props')}
        </button>
        <button
          type="button"
          className={activeTab === 'root' ? styles['__active'] : ''}
          onClick={() => setActiveTab('root')}
        >
          {t('Root fields')}
        </button>
        <button
          type="button"
          className={activeTab === 'dsi' ? styles['__active'] : ''}
          onClick={() => setActiveTab('dsi')}
        >
          {t('DSI fields')}
        </button>
        <button
          type="button"
          className={activeTab === 'aliases' ? styles['__active'] : ''}
          onClick={() => setActiveTab('aliases')}
        >
          {t('Aliases')}
        </button>
      </div>

      <div className={styles.UiConfigDocsPanel_Body}>
        <EntryList docs={tabDocs} search={search} highlightKey={highlightKey} />
      </div>
    </div>
  )
}
